package com.macrofocus.eflux.ui.morphing

import org.locationtech.jts.geom.Coordinate
import org.locationtech.jts.geom.LineString
import org.locationtech.jts.geom.LinearRing
import org.locationtech.jts.geom.Polygon
import org.locationtech.jts.linearref.LengthIndexedLine

fun combineLineStringAtRelativeDistances(
    line1: LineString,
    line2: LineString,
): Pair<LineString, LineString> {
    val length1 = line1.length
    val length2 = line2.length

    // Use LengthIndexedLine for interpolation
    val indexer1 = LengthIndexedLine(line1)
    val indexer2 = LengthIndexedLine(line2)

    // Collect relative distances (fractions) from both linestrings
    val relativeDistances = mutableSetOf<Double>()

    // Add fractions from line1
    for (i in 1 until line1.numPoints - 1) {
        val distance = line1.getPointN(i).distance(line1.startPoint!!)
        val fraction = if (length1 > 0) distance / length1 else 0.0
        relativeDistances.add(fraction.coerceIn(0.0, 1.0))
    }

    // Add fractions from line2
    for (i in 1 until line2.numPoints - 1) {
        val distance = line2.getPointN(i).distance(line2.startPoint!!)
        val fraction = if (length2 > 0) distance / length2 else 0.0
        relativeDistances.add(fraction.coerceIn(0.0, 1.0))
    }

    // Sort the combined distances
    val sortedDistances = relativeDistances.sorted()

    // Interpolate points on both linestrings using the combined distances
    val coords1 = mutableListOf<Coordinate>()
    val coords2 = mutableListOf<Coordinate>()

    coords1.add(line1.startPoint!!.coordinate!!)
    coords2.add(line2.startPoint!!.coordinate!!)
    for (fraction in sortedDistances) {
        val dist1 = fraction * length1
        val dist2 = fraction * length2

        // Extract coordinates at the specified distances
        coords1.add(indexer1.extractPoint(dist1))
        coords2.add(indexer2.extractPoint(dist2))
    }
    coords1.add(line1.endPoint!!.coordinate!!)
    coords2.add(line2.endPoint!!.coordinate!!)

    return Pair(
        line1.factory.createLineString(coords1.toTypedArray()),
        line2.factory.createLineString(coords2.toTypedArray())
    )
}

fun combineLinearRingAtRelativeDistances(
    ring1: LinearRing,
    ring2: LinearRing,
): Pair<LinearRing, LinearRing> {
    val line1 = ring1
    val line2 = ring2

    val length1 = line1.length
    val length2 = line2.length

    // Use LengthIndexedLine for interpolation
    val indexer1 = LengthIndexedLine(line1)
    val indexer2 = LengthIndexedLine(line2)

    // Collect relative distances (fractions) from both linestrings
    val relativeDistances = mutableSetOf<Double>()

    // Add fractions from line1
    for (i in 1 until line1.numPoints - 1) {
        val distance = indexer1.project(line1.getPointN(i).coordinate!!)
        val fraction = if (length1 > 0) distance / length1 else 0.0
        relativeDistances.add(fraction.coerceIn(0.0, 1.0))
    }

    // Add fractions from line2
    for (i in 1 until line2.numPoints - 1) {
        val distance = indexer2.project(line2.getPointN(i).coordinate!!)
        val fraction = if (length2 > 0) distance / length2 else 0.0
        relativeDistances.add(fraction.coerceIn(0.0, 1.0))
    }

     // Interpolate points on both linestrings using the combined distances
    val coords1 = mutableListOf<Coordinate>()
    val coords2 = mutableListOf<Coordinate>()

    coords1.add(line1.startPoint!!.coordinate!!)
    coords2.add(line2.startPoint!!.coordinate!!)

    if(relativeDistances.isNotEmpty()) {
        // Sort the combined distances
        val sortedDistances = relativeDistances.sorted()

        for (fraction in sortedDistances) {
            // Extract coordinates at the specified distances
            if(fraction == 0.0) {
            } else if(fraction == 1.0) {
            } else {
                val dist1 = fraction * length1
                val dist2 = fraction * length2

                coords1.add(indexer1.extractPoint(dist1))
                coords2.add(indexer2.extractPoint(dist2))
            }
        }
    }
    coords1.add(line1.endPoint!!.coordinate!!)
    coords2.add(line2.endPoint!!.coordinate!!)

    return Pair(
        line1.factory.createLinearRing(coords1.toTypedArray()),
        line2.factory.createLinearRing(coords2.toTypedArray())
    )
}

fun mergePointsAtRelativeDistances(
    line1: Polygon,
    line2: Polygon,
): Pair<Polygon, Polygon> {
    val (linearRing1, linearRing2) = combineLinearRingAtRelativeDistances(line1.exteriorRing!!, line2.exteriorRing!!)

    return Pair(
        line1.factory.createPolygon(linearRing1, null),
        line2.factory.createPolygon(linearRing2, null)
    )
}
